import { Box, Breadcrumbs, Grid, Link, Typography } from "@mui/material";
import { Link as RouterLink, Outlet, useOutletContext } from "react-router-dom";
import { Home as HomeIcon } from "@mui/icons-material";
import { TabNavigation, useRouteMatch } from "~/components/tab-nav";
import { roleBillingManager } from "~/lib/iam/roles/BillingManager";
import { useCanListWifAuthBindings } from "~/lib/iam/actions/mondoo/captain/Captain/ListWIFAuthBindings";
import { useScope } from "~/hooks/useScope";
import { Org } from "~/lib/types";
import { TestIamActionsQuery } from "~/operations";
import { setDocumentTitle } from "~/lib/commonUtils";

export type OrganizationSettingsPageProps = {
  org: Org;
  availablePermissions: string[];
};

type OrgSettingsOutletContextType = {
  availablePermissions: TestIamActionsQuery["testIamActions"];
  org: Org;
};

export function OrganizationSettingsPage({
  org,
  availablePermissions,
}: OrganizationSettingsPageProps) {
  const hasBillingPermissions = roleBillingManager.allowedActions.every(
    (action) => availablePermissions.includes(action),
  );
  const { organizationScope } = useScope();
  const hasWIFListPermissions = useCanListWifAuthBindings(organizationScope);

  const href = (tab: string) => {
    return `/organization/settings/${tab}?organizationId=${org.id}`;
  };

  const generalTab = {
    label: "General Settings",
    to: href("general"),
    route: "/general",
  };

  const unsortedTabs = [
    { label: "Members", to: href("members"), route: "/members" },
    { label: "Audit log", to: href("auditlog"), route: "/auditlog" },
    {
      label: "Authentication",
      to: href("authentication"),
      route: "/authentication",
    },
    { label: "API Tokens", to: href("api-tokens"), route: "/api-tokens" },
    {
      label: "Service Accounts",
      to: href("serviceaccounts"),
      route: "/serviceaccounts",
    },
    hasBillingPermissions
      ? { label: "Billing", to: href("billing"), route: "/billing" }
      : null,
    { label: "Legal & Compliance", to: href("legal"), route: "/legal" },
    hasWIFListPermissions
      ? {
          label: "Workload Identity",
          to: href("workload-identity"),
          route: "/workload-identity",
        }
      : null,
  ].flatMap((tab) => tab ?? []);

  const otherTabs = unsortedTabs.sort((a, b) =>
    a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
  );

  const tabList = [generalTab, ...otherTabs];

  const currentTab = useRouteMatch(
    tabList.map((x) => x.route),
    "general",
  );

  const currentTabLabel =
    tabList.find((tab) => tab.route.endsWith(currentTab))?.label || "";

  const breadcrumbs = [
    <Link
      key="/organization/overview"
      component={RouterLink}
      to={`/organization/overview?organizationId=${org.id}`}
      display="flex"
    >
      <HomeIcon fontSize="inherit" />
    </Link>,
    <Typography key="/organization/settings">Settings</Typography>,
  ];

  setDocumentTitle([currentTabLabel, "Settings", org.name]);

  return (
    <Grid container spacing={3}>
      <Grid size={12}>
        <Breadcrumbs sx={{ mb: 3, overflowWrap: "anywhere" }} separator="›">
          {breadcrumbs}
        </Breadcrumbs>
        {/* Heading */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            Organization Settings
          </Typography>
        </Box>
        <TabNavigation
          {...{ id: "org-settings-tabs", currentTab, tabList, marginBottom: 1 }}
        />
      </Grid>
      <Grid size="grow">
        <Outlet context={{ availablePermissions, org }} />
      </Grid>
    </Grid>
  );
}

export function useOrgSettingsOutletContext() {
  return useOutletContext<OrgSettingsOutletContextType>();
}
